import axios, { AxiosResponse } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

interface SupportedCurrency {
    network: string;
    currency: string;
    min_deposit: number;
    max_deposit: number;
    is_enabled: boolean;
    icon_url?: string;
    provider_name: string;
}

interface AddressData {
    network: string;
    currency: string;
    wallet_address: string;
    limits: {
        min: number;
        max: number;
    };
}

interface HasSelectedCardResponse {
    has_selected_card: boolean;
    card_details: Card | null;
}

interface Card {
    id: number;
    holder_name: string;
    last4: string;
    createdAt: string;
}

interface ApiResponse<T> {
    statusCode: number;
    data?: T;
    message?: string;
}

class ApiService {
    private baseURL: string;
    private maxRetries: number = 3;
    private retryDelay: number = 1000; // 1 second

    constructor() {
        this.baseURL = API_BASE_URL;
    }

    private async makeRequest<T>(
        method: 'GET' | 'POST' | 'PUT' | 'DELETE',
        endpoint: string,
        token: string,
        data?: any,
        retryCount: number = 0
    ): Promise<T> {
        if (!token) {
            throw new Error('Access token is required');
        }

        try {
            const config = {
                method,
                url: `${this.baseURL}${endpoint}`,
                headers: {
                    'Content-Type': 'application/json'
                },
                params: { token, ...(method === 'GET' ? { ...data } : {}) },
                data: method !== 'GET' ? { ...data } : undefined,
                timeout: 10000, // 10 second timeout
            };

            const response: AxiosResponse<ApiResponse<T>> = await axios(config);

            if (response.data.statusCode == 1) {
                return response.data.data as T;
            } else {
                throw new Error(response.data.message || `API request failed with status ${response.data.statusCode}`);
            }
        } catch (error: any) {
            // Retry logic for network errors
            if (retryCount < this.maxRetries && this.shouldRetry(error)) {
                await this.delay(this.retryDelay * (retryCount + 1)); // Exponential backoff
                return this.makeRequest<T>(method, endpoint, token, data, retryCount + 1);
            }

            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            if (error.response?.status) {
                throw new Error(`HTTP ${error.response.status}: ${error.response.statusText}`);
            }
            if (error.code === 'ECONNABORTED') {
                throw new Error('Request timeout - please check your connection');
            }
            throw new Error(error.message || 'Network error');
        }
    }

    private shouldRetry(error: any): boolean {
        // Retry on network errors, timeouts, and 5xx server errors
        return !error.response ||
            error.response.status >= 500 ||
            error.code === 'ECONNABORTED' ||
            error.code === 'NETWORK_ERROR';
    }

    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async getSupportedCurrencies(token: string): Promise<SupportedCurrency[]> {
        return this.makeRequest<SupportedCurrency[]>('GET', '/v2/dedicated-address/supportedCurrencies', token);
    }

    async getUserAddress(token: string, network: string, currency: string): Promise<AddressData> {
        return this.makeRequest<AddressData>('GET', '/v2/dedicated-address/myAddress', token, {
            network,
            currency,
        });
    }

    async generateAddress(token: string, network: string, currency: string): Promise<AddressData> {
        return this.makeRequest<AddressData>('POST', '/v2/dedicated-address/update', token, {
            network,
            currency,
            wallet_address: '', // Will be generated by the service
        });
    }

    async updateAddress(token: string, network: string, currency: string, walletAddress: string): Promise<AddressData> {
        return this.makeRequest<AddressData>('PUT', '/v2/dedicated-address/update', token, {
            network,
            currency,
            wallet_address: walletAddress,
        });
    }

    async hasSelectedCard(token: string): Promise<HasSelectedCardResponse> {
        return this.makeRequest<HasSelectedCardResponse>('GET', '/v2/dedicated-address/hasSelectedCard', token);
    }

    async listCards(token: string): Promise<Card[]> {
        return this.makeRequest<Card[]>('GET', '/v2/dedicated-address/listCards', token);
    }

    async setDefaultCard(token: string, cardId: number): Promise<void> {
        return this.makeRequest<void>('POST', '/v2/dedicated-address/setDefaultCard', token, { card_id: cardId });
    }
}

export const apiService = new ApiService();
